import * as api from "@/services/api";
import { serverErrorMessage } from "@/services/helpers";

// used in InventoryTable, InventoryTableQuantity, InventoryBatchesTableQuantity
export const fetchAllSingleProductData = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.get(`/inventory/${payload.id}`, payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Fetching Product" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Fetching Product") }, { root: true });
    return error;
  }
};

// used in InventoryTablePrice, inventory.js
export const updateInventoryItemListing = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/inventory/${payload.id}`, payload);
    commit("replaceItem", response.data.data);
    commit("ui/setSnackbar", { variant: "success", message: "Product Updated Successfully!" }, { root: true });
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Product") }, { root: true });
  }
};

// used in BatchTable, InventoryBatches, InventoryBatchesArchived
// Note: Local state is updated before API call, so we don't need to update the store here.
// The response now returns only the updated batches (not the full product) for performance.
export const updateBatchSortOrder = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/batches/update-sort-order`, payload);
    if (response.data.code == 200) {
      // Sort order update successful - local state already reflects the change
      return response.data.data;
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Updating Batch" }, { root: true });
      return null;
    }
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Batch") }, { root: true });
    return error;
  }
};

// used in InventoryModal, ProductBatchesFull
export const bulkUpdateBatchPricing = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`inventory/batch/bulkUpdatePrice/${payload.product_id}`, payload);
    if (response.data && response.data.data) {
      commit("replaceItem", response.data.data);
      // commit("replaceBatches", { product: response.data.data.id, batches: response.data.data.batches });
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Updating Batch" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Batch") }, { root: true });
    return error;
  }
};

// used in inventorymodal.vue
export const duplicateProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.post("/inventory/product/duplicate", payload);
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Creating Product") }, { root: true });
    return error;
  }
};

// used in TransferBatchModal.vue; resolves whether the split went through
export const handleBatchSplit = async ({ commit }, payload) => {
  try {
    const response = await api.post("/inventory/batch/split", payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    }
    return true;
  } catch (error) {
    let message = serverErrorMessage(error, "Error Splitting Batch");
    if (message == "Original batch quantity not sufficient for this split") {
      const product = error.response.data.product?.original?.data;
      if (product) {
        commit("replaceItem", product);
      }
      message = "Batch quantity was not sufficient. We refetched it, please retry.";
    }
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message }, { root: true });
    return false;
  }
};

// used in TransferBatchModal.vue; resolves whether the transfer went through
export const handleBatchTransfer = async ({ commit }, payload) => {
  try {
    const response = await api.post("/inventory/batch/transfer", payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    }
    return true;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Transferring Batch") }, { root: true });
    return false;
  }
};

// Reload the base lookups a product form payload created by typing in new values.
export const refreshTypedLookups = ({ dispatch }, productDetails) => {
  if (!productDetails) {
    return;
  }
  const fetches = {
    newContainerType: "base/fetchContainerTypes",
    newFloweringPeriod: "base/fetchFloweringPeriods",
    newCultivar: "base/fetchCultivars",
    newProductFlavor: "base/fetchProductFlavors",
    newStorageType: "base/fetchStorageTypes",
  };
  Object.entries(fetches).forEach(([key, action]) => {
    if (productDetails[key] && productDetails[key].name) {
      dispatch(action, "", { root: true });
    }
  });
  if (productDetails.environmentalIssues && productDetails.environmentalIssues.some((issue) => !issue.id)) {
    dispatch("base/fetchEnvironmentalIssues", "", { root: true });
  }
};

// used in InventoryModal.vue
export const createProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.post("/inventory/product/create", payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    }

    dispatch("refreshTypedLookups", payload.productDetails);

    return response.data.data;
  } catch (error) {
    let message =
      error.response && error.response.data && error.response.data.message ? error.response.data.message : "There was an error creating the product";
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: message }, { root: true });
    return error;
  }
};

export const createMetrcMappedProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.post("/metrc/operation/items/create-product", payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    }

    dispatch("refreshTypedLookups", payload.productDetails);

    commit("ui/setSnackbar", { variant: "success", message: "Product created and mapped to the Metrc item" }, { root: true });

    return response.data.data;
  } catch (error) {
    const message = error.response?.data?.message || "There was an error creating and mapping the product";
    commit("ui/setSnackbar", { variant: "danger", title: "Product not created", message }, { root: true });
    return error;
  }
};

//used in InventoryModal.vue
export const updateProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/inventory/product/update/${payload.product.id}`, payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
    }
    dispatch("refreshTypedLookups", payload.productDetails);
    commit("ui/setSnackbar", { variant: "success", message: "Product Saved" }, { root: true });
    return response.data.data;
  } catch (error) {
    commit(
      "ui/setSnackbar",
      { variant: "danger", title: "Error", message: serverErrorMessage(error, "There was an error updating the product") },
      { root: true },
    );
    return error;
  }
};

// used in BatchTableLineItem, InventoryBatchesPrice, InventoryBatchesTableQuantity
// note this doesn't handle the relationships, its used for line item stuff at batch level
export const updateInventoryItemBatchListing = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/batches/single/${payload.id}`, payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
      const message = response.data.message || "Batch Updated Successfully";
      commit("ui/setSnackbar", { variant: "success", message }, { root: true });
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Updating Batch" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Batch") }, { root: true });
    return error;
  }
};

// no relations returned
export const simpleBatchUpdate = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/batches/simple/${payload.id}`, payload);
    if (response.data.code == 200) {
      // commit("replaceItem", response.data.data);
      const message = response.data.message;
      if (message && message.length > 0 && message != "Batch Updated") {
        commit("ui/setSnackbar", { variant: "success", message }, { root: true });
      }
    } else {
      commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Updating Batch" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Batch") }, { root: true });
    return error;
  }
};

// used in BatchForm.vue
export const fullUpdateExistingBatch = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/batches/single/full/${payload.batch.id}`, payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
      const message = response.data.message || "Batch Updated Successfully";
      commit("ui/setSnackbar", { variant: "success", message }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Batch") }, { root: true });
    return error;
  }
};

export const setQuickOpenBatch = ({ commit }, payload) => {
  commit("setQuickOpenBatch", payload);
};

// used in BatchForm.vue
export const addNewBatchForExistingProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.post("/batches/add", payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
      commit("ui/setSnackbar", { variant: "success", message: "Batch Added" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Adding Batch") }, { root: true });
    return error;
  }
};

// used in SecureQuantityAdjustment.vue
export const safeUpdateBatchQuantity = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.patch(`/batches/safe-update-quantity/${payload.id}`, payload);
    if (response.data.code == 200) {
      commit("replaceItem", response.data.data);
      commit("ui/setSnackbar", { variant: "success", message: "Success" }, { root: true });
    }
    return response.data.data;
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Updating Quantity") }, { root: true });
    return error;
  }
};

export const clearInventory = async ({ commit, dispatch }) => {
  try {
    commit("clear");
  } catch (error) {
    //
  }
};

export const resetInventory = async ({ commit, dispatch }) => {
  try {
    commit("reset");
  } catch (error) {
    //
  }
};

export const processAllProductData = async ({ commit, dispatch }, payload) => {
  try {
    commit("base/initProductData", payload.productFieldData, { root: true });
    commit("initProducts", payload.products);
  } catch (error) {
    commit(
      "ui/setSnackbar",
      {
        variant: "danger",
        title: "Error",
        message: serverErrorMessage(error, "Error Processing Product and Fields Data"),
      },
      { root: true },
    );
  }
};

export const injectItemIntoProduct = async ({ commit, dispatch }, payload) => {
  try {
    const response = await api.post("/inventory/product/inject", payload);
    if (response.data && response && response.data.code == 200) {
      if (response.data.data.newProduct) {
        commit("productAdded", response.data.data.newProduct);
      }
      if (response.data.data.updatedProduct) {
        commit("replaceItem", response.data.data.updatedProduct);
      }
      commit("ui/setSnackbar", { variant: "success", message: "Successfully Added to Inventory" }, { root: true });
      return response.data.data;
    }
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: "Error Adding Product" }, { root: true });

    return [];
  } catch (error) {
    commit("ui/setSnackbar", { variant: "danger", title: "Error", message: serverErrorMessage(error, "Error Adding Product") }, { root: true });
    return error;
  }
};
